import { useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";

import { getApiBaseUrl } from "@/api";

import Services from "./Services";
const LAST_BANNER_VIEWED_TIME_IN_TIMESTAMP =
  "repeatmd-app-last-idle-time-in-timestamp";
export const APPSFLYER_DOMAIN = "repeatmd.onelink.me";
export const APPSFLYER_LINK = `${APPSFLYER_DOMAIN}/zZOz`;
export const LINK_TYPES = {
  BANNER: "banner",
  CHECKOUT: "checkout",
};

export const setIdleTimeInTimestamp = (userId: string | undefined) => {
  if (!userId) {
    return;
  }
  const lastIdleTime = {
    date: dayjs().format("MM-DD-YYYY"),
    userId: userId,
  };

  localStorage.setItem(
    LAST_BANNER_VIEWED_TIME_IN_TIMESTAMP + "-" + userId,
    JSON.stringify(lastIdleTime)
  );
};

export const getAppsFlyerParams = async (
  uri: string,
  linkType: string,
  platformId?: string
) => {
  const token = await Services.auth.getToken();
  return {
    deep_link_sub1: uri,
    deep_link_sub2: token || "",
    deep_link_value: platformId || "",
    af_force_deeplink: "1",
    af_web_dp: "https://amora.repeatmd.app",
    pid: "rmd_media_source",
    af_xp: "custom",
    c: linkType === LINK_TYPES.BANNER ? "banner" : "checkout",
  };
};
export const getLinkToMobileApp = async (
  linkType: string,
  path: string,
  platformId: string | undefined
) => {
  if (!platformId) {
    return;
  }
  const params = {
    platformId,
  };
  const baseUrl = getApiBaseUrl() || location.origin;
  const uri = `${baseUrl}${path}${
    path.includes("?") ? "&" : "?"
  }${new URLSearchParams(params).toString()}`;

  const mobileAppLink = new URL(`https://${APPSFLYER_LINK}`);

  const appsFlyerParams = await getAppsFlyerParams(uri, linkType, platformId);
  mobileAppLink.search = new URLSearchParams(appsFlyerParams).toString();
  return mobileAppLink;
};

export const showViewAppFooter = (userId: string | undefined) => {
  if (!userId) {
    return false;
  }
  const lastTimeStamp = JSON.parse(
    localStorage.getItem(LAST_BANNER_VIEWED_TIME_IN_TIMESTAMP + "-" + userId) ||
      "{}"
  );
  // We show app banner if user hasn't seen it before (no timestamp)
  if (Object.keys(lastTimeStamp).length == 0) {
    return true;
  }

  const lastViewedDate = dayjs(lastTimeStamp.date);
  const today = dayjs();
  const isNewDay = today.diff(lastViewedDate, "days") > 0;

  // We show app banner if it's a different day
  if (isNewDay) {
    return true;
  }

  return false;
};

export const useAppsFlyerBannerDetection = () => {
  const { data: bannerExists } = useQuery({
    staleTime: Infinity,
    cacheTime: Infinity,
    retry: 50,
    retryDelay: 100,
    queryKey: ["smart-banner"],
    queryFn: () => {
      if (document.getElementById("smart-banner")) return true;
      else throw new Error();
    },
  });

  return bannerExists ?? false;
};

export const openLinkToMobileApp = async (
  platformId: string | undefined,
  referralCode: string | null
) => {
  const path = referralCode
    ? `/signin?referralCode=${referralCode}`
    : "/signin";
  const linkToMobileApp = await getLinkToMobileApp(
    LINK_TYPES.BANNER,
    path,
    platformId
  );
  if (linkToMobileApp) {
    window.location.replace(linkToMobileApp.href);
  }
};

export const getReferralCodeFromUrl = (url: string) => {
  const referralCodeFromUrl = url.split("referralCode=")?.[1];

  // When opening the app via a deep link from the FriendReferralLandingPage the url is `/referral/:referralCode`
  // and `referralCode` is not present as a search param.
  if (!referralCodeFromUrl && url.includes("/referral/")) {
    const parts = url.split("/");
    return parts[parts.length - 1];
  }
  return referralCodeFromUrl;
};
